// Pure helpers for pieces that have several players (more players than pieces).
// Two layouts exist:
//  - SLICED   (Live mode): the piece was divided into sub_count sub-slots when the
//             drawing phase started; every player draws their own slice, and the
//             slices tile the piece exactly (see utils/subSlots.js).
//  - GRID     (fallback: Self-Paced wrap-around, or data from before the freeze):
//             players share the whole piece, shown as a small grid of portraits.
// No React / Firebase imports so layout and grouping can be unit-tested.

import { subSliceRect, dividePiecesEnabled, hashString } from './subSlots';

export const MAX_PORTRAITS = 9;
const DONE_STATUSES = new Set(['submitted', 'auto_filled']);

export function isDoneStatus(status) {
  return DONE_STATUSES.has(status);
}

/**
 * { [tileId]: [user, ...] } — EVERY player on a piece, in a stable order
 * (name, then userId) so portraits don't shuffle each time a Firebase node
 * updates. `key` is the Firebase node key (needed to look up that player's
 * live preview). Entries without a valid puzzle_id are ignored.
 */
export function groupUsersByTile(usersMap) {
  const byTile = {};
  if (!usersMap || typeof usersMap !== 'object') return byTile;
  Object.entries(usersMap).forEach(([key, data]) => {
    const tileId = Math.floor(Number(data?.puzzle_id));
    if (!Number.isFinite(tileId) || tileId < 1) return;
    (byTile[tileId] = byTile[tileId] || []).push({ ...data, userId: data?.userId ?? key, key });
  });
  Object.values(byTile).forEach((list) =>
    list.sort((a, b) => {
      const byName = String(a.name || '').toLowerCase().localeCompare(String(b.name || '').toLowerCase());
      return byName !== 0 ? byName : String(a.userId).localeCompare(String(b.userId));
    })
  );
  return byTile;
}

/**
 * LIVE board: ONE image per piece, however many players share it.
 *   - a piece the VIEWER is on shows the viewer's own drawing;
 *   - every other piece shows one of its players, picked "at random" but as a pure function of
 *     (viewer, piece): different viewers see different people, a refresh or a Firebase update never
 *     changes the pick, and a player joining only changes it if the hash lands on them.
 * `groups` is groupUsersByTile(...) ({ [tileId]: [user, ...] }, stable order). Returns { [tileId]: user }.
 */
export function pickRepresentatives(groups, viewerId) {
  const viewer = viewerId == null ? '' : String(viewerId);
  const out = {};
  Object.entries(groups || {}).forEach(([tileId, list]) => {
    if (!Array.isArray(list) || list.length === 0) return;
    const me = viewer ? list.find((u) => String(u.userId) === viewer) : null;
    out[tileId] = me || list[hashString(`${viewer}|${tileId}`) % list.length];
  });
  return out;
}

/**
 * Whose live drawing (Firebase preview node) the viewer subscribes to: the representatives that are still
 * drawing. The viewer's own piece first, then the others in a fixed per-viewer order, cut at `limit`
 * (a big room watches fewer, as before). Returns Firebase node keys.
 */
export function repWatchKeys(representatives, viewerId, limit = 12) {
  const viewer = viewerId == null ? '' : String(viewerId);
  const candidates = Object.entries(representatives || {})
    .map(([tileId, user]) => ({ tileId, user }))
    .filter(({ user }) => user && user.key && !isDoneStatus(user.status));
  const own = candidates.filter(({ user }) => String(user.userId) === viewer);
  const rest = candidates
    .filter(({ user }) => String(user.userId) !== viewer)
    .map((c) => ({ ...c, score: hashString(`${viewer}|${c.tileId}`) }))
    .sort((a, b) => a.score - b.score || Number(a.tileId) - Number(b.tileId));
  return [...own, ...rest].slice(0, Math.max(0, limit)).map(({ user }) => user.key);
}

/**
 * FINAL picture (reveal page): which player's image fills a piece. One piece = one image, never a grid of
 * copies, so the finished artwork reads as a whole.
 *   1. the VIEWER's OWN piece shows the viewer's own drawing - and only that: if the viewer did not draw
 *      (blank, auto-filled), it shows the coloured part (null), never a teammate's drawing;
 *   2. any other piece shows ONE of the players who did draw, picked at random PER VIEWER: of 5 players on a piece
 *      where 3 drew, each viewer gets one of those 3, so every player sees a different final picture. The
 *      pick is a pure function of (viewer, piece), so it never changes on refresh and the downloaded image
 *      matches the screen;
 *   3. otherwise null: nobody on this piece drew, and the caller shows the coloured (master) piece.
 * Only a real drawing counts (status 'submitted' with an image). An 'auto_filled' player never drew: their
 * tile is just the coloured piece, which is what case 3 shows anyway.
 */
export function pickRevealContributor(contributors, currentUserId, pieceId = '') {
  const list = Array.isArray(contributors) ? contributors : [];
  const drew = (u) => u && u.status === 'submitted' && Boolean(u.tileImageKey);
  const viewerHere = currentUserId != null ? list.find((u) => String(u.userId) === String(currentUserId)) : null;
  if (viewerHere) return drew(viewerHere) ? viewerHere : null;
  const drawn = list.filter(drew).sort((a, b) => String(a.userId).localeCompare(String(b.userId)));
  if (drawn.length === 0) return null;
  const viewer = currentUserId == null ? '' : String(currentUserId);
  return drawn[hashString(`${viewer}|reveal|${pieceId}`) % drawn.length];
}

/**
 * The viewer's own finished tile as the BACKEND recorded it (getGameStages: puzzle_id, tile_status,
 * tile_s3_key), merged into the Firebase players map. The canvas tells Firebase about a finished drawing in
 * a fire-and-forget write; when that write was lost, the reveal did not know the viewer had drawn and showed
 * the coloured piece instead of their own picture. The backend record is authoritative, so it wins - but only
 * when it says the tile is finished. Returns a new map (or the same one when there is nothing to add).
 */
export function mergeOwnGame(usersMap, viewerId, game, { name = '' } = {}) {
  const map = usersMap && typeof usersMap === 'object' ? usersMap : {};
  if (viewerId == null || !game || !isDoneStatus(game.tile_status) || !game.tile_s3_key) return map;
  const viewer = String(viewerId);
  const patch = {
    status: game.tile_status,
    tileImageKey: game.tile_s3_key,
    ...(game.puzzle_id != null && Number.isFinite(Number(game.puzzle_id)) ? { puzzle_id: Number(game.puzzle_id) } : {}),
  };
  const hit = Object.entries(map).find(([key, u]) => String((u && u.userId) ?? key) === viewer);
  if (hit) {
    const [key, current] = hit;
    if (current.status === patch.status && current.tileImageKey === patch.tileImageKey && (patch.puzzle_id == null || Number(current.puzzle_id) === patch.puzzle_id)) {
      return map;
    }
    return { ...map, [key]: { ...current, ...patch } };
  }
  return { ...map, [viewer]: { userId: viewer, name, ...patch } };
}

function progressRank(user) {
  const done = isDoneStatus(user?.status) ? 1 : 0;
  return done * 1e13 + (Number(user?.updatedAt) || 0);
}

/**
 * Sliced layout for one piece, or null when the piece isn't divided.
 * Returns { count, slots: [{ index, rect, occupants, rep }] } with one entry for
 * EVERY sub-slot (so an unfilled slice is still drawn). `rep` is the occupant
 * shown in the slot: a finished one wins, otherwise the most recently active.
 * Several occupants only happen for players who joined after the division froze.
 */
export function buildSlots(contributors) {
  // Pieces are shared whole unless division is switched on, whatever stale slice values players carry.
  if (!dividePiecesEnabled()) return null;
  const list = Array.isArray(contributors) ? contributors : [];
  const counts = list.map((u) => Math.floor(Number(u?.sub_count)) || 0);
  const count = Math.max(0, ...counts);
  if (count < 2) return null;

  const slots = Array.from({ length: count }, (_, index) => ({
    index,
    rect: subSliceRect(index, count),
    occupants: [],
    rep: null,
  }));
  list.forEach((u) => {
    const index = Math.floor(Number(u?.sub_index));
    if (Number.isFinite(index) && index >= 0 && index < count) slots[index].occupants.push(u);
  });
  slots.forEach((slot) => {
    slot.rep = [...slot.occupants].sort((a, b) => progressRank(b) - progressRank(a))[0] || null;
  });
  return { count, slots };
}

/**
 * What one slot shows. `crop` = the source is the WHOLE piece (outline / master /
 * an auto-fill), so only this slot's rectangle of it should be visible; a
 * submitted tile or live preview is already just this player's slice.
 *   resolveKey(key) -> URL for a stored image key.
 */
export function slotImage(rep, { previews = {}, masterSrc = null, outlineSrc = null, useMasterFallback = false, resolveKey }) {
  const status = rep?.status;
  if (rep && isDoneStatus(status) && rep.tileImageKey) {
    return { src: resolveKey(rep.tileImageKey), crop: status === 'auto_filled', dim: false };
  }
  const preview = rep ? previews[rep.key] : null;
  if (rep && !isDoneStatus(status) && preview) {
    return { src: preview, crop: false, dim: false };
  }
  if (useMasterFallback && masterSrc) {
    return { src: masterSrc, crop: true, dim: false };
  }
  return { src: outlineSrc, crop: true, dim: !rep };
}

/** Grid for n portraits inside one piece: 1 -> 1x1, 2 -> 2x1, 3-4 -> 2x2, 5-6 -> 3x2, 7-9 -> 3x3. */
export function portraitGridShape(n) {
  const count = Math.max(1, Math.floor(Number(n)) || 1);
  const cols = Math.ceil(Math.sqrt(count));
  return { cols, rows: Math.ceil(count / cols) };
}

/** Caps how many portraits are drawn in one piece; the rest are summarised as "+N". */
export function splitVisible(list, cap = MAX_PORTRAITS) {
  const all = Array.isArray(list) ? list : [];
  return { shown: all.slice(0, cap), hidden: Math.max(0, all.length - cap) };
}

export function contributorNames(list) {
  return (Array.isArray(list) ? list : [])
    .map((u) => String(u?.name || '').trim())
    .filter(Boolean)
    .join(', ');
}

/** Board stats: a piece counts as completed only when ALL of its players are done. */
export function tileProgress(groups, totalTiles) {
  let participants = 0;
  let completed = 0;
  for (let id = 1; id <= totalTiles; id += 1) {
    const list = groups?.[id];
    if (!list || list.length === 0) continue;
    participants += list.length;
    if (list.every((u) => isDoneStatus(u.status))) completed += 1;
  }
  const percent = totalTiles > 0 ? Math.round((completed / totalTiles) * 100) : 0;
  return { participants, completed, total: totalTiles, percent };
}

/** Sub-rectangles of the cell (x, y, w, h) for n portraits (GRID layout), row-major. */
export function portraitRects(n, x, y, w, h) {
  const { cols, rows } = portraitGridShape(n);
  const cw = w / cols;
  const ch = h / rows;
  return Array.from({ length: n }, (_, i) => ({
    x: x + (i % cols) * cw,
    y: y + Math.floor(i / cols) * ch,
    w: cw,
    h: ch,
  }));
}

/** Absolute rectangle of a slot's fractional rect inside a cell (x, y, w, h) — SLICED layout. */
export function slotRectInCell(rect, x, y, w, h) {
  return { x: x + rect.x * w, y: y + rect.y * h, w: rect.w * w, h: rect.h * h };
}
